import { Avatar } from "./ui/Avatar";
import { RichTextInput, type RichTextInputHandle } from "./RichTextInput";
import { SubmitHint, SubmitTooltip } from "./SubmitShortcut";
import { AttachmentTray, AddAttachmentButton } from "./AttachmentTray";
import type { useAttachments } from "@/lib/hooks/useAttachments";
import type { CurrentUser } from "@/lib/hooks/useCurrentUser";
import type { RefObject } from "react";
import { cn } from "./ui/cn";

/**
 * The comment-composer row: avatar, mention input, attachments, anonymous
 * toggle, send button and the shortcut hint.
 *
 * USED BY BOTH THE CARD AND THE LIGHTBOX. The lightbox used to carry its own
 * copy of this markup, which is how it came to be the one comment box with no
 * shortcut hint (OSO-532) and no send button at all (OSO-534) — both were added
 * here and the copy silently stayed behind. Two props are all the difference
 * amounted to, so there is no second copy to fall behind any more.
 */
/**
 * The row's frame — shared with the placeholder that holds this form's place
 * on a server-rendered card while the browser's first `GET /me` is in flight
 * (`PostCard`, OSO-675), so the two cannot drift apart and the swap cannot
 * move the layout.
 */
export const COMMENT_FORM_FRAME =
  "mx-gutter border-t border-border py-3 flex items-start gap-2";

export function PostCommentForm({
  mentionRef,
  currentUser,
  commentAnonymous,
  setCommentAnonymous,
  commentText,
  setCommentText,
  groupId,
  groupSlug,
  submitComment,
  commentFiles,
  isSubmitting,
  canSubmitComment,
  anonymousAllowed,
  error,
  onEscape,
  className,
}: {
  mentionRef: RefObject<RichTextInputHandle | null>;
  currentUser: CurrentUser;
  commentAnonymous: boolean;
  setCommentAnonymous: (fn: (v: boolean) => boolean) => void;
  commentText: string;
  setCommentText: (value: string) => void;
  groupId: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  submitComment: () => void;
  commentFiles: ReturnType<typeof useAttachments>;
  isSubmitting: boolean;
  canSubmitComment: boolean;
  anonymousAllowed: boolean;
  /** What the server said when it refused the last attempt, if it did. */
  error?: string | null;
  /**
   * Escape, where the surface itself answers to it. The lightbox passes a blur:
   * without one the key falls through to the handler that closes the lightbox,
   * and it came down mid-sentence. `RichTextInput` marks the key spent when
   * this fires, which is what stops that.
   */
  onEscape?: () => void;
  /** Extra classes for the row — the lightbox needs `shrink-0` in its column. */
  className?: string;
}) {
  return (
    <div className={cn(COMMENT_FORM_FRAME, className)}>
      {commentAnonymous ? (
        <div className="shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500 dark:text-purple-400">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </div>
      ) : (
        <Avatar
          url={currentUser.avatar_template?.replace("{size}", "48")}
          name={currentUser.name || currentUser.username}
          size="sm"
        />
      )}
      <form
        className="flex-1 group"
        onSubmit={(e) => {
          e.preventDefault();
          submitComment();
        }}
      >
        <div className="relative">
          <RichTextInput
            ref={mentionRef}
            ariaLabel="Skriv en kommentar"
            value={commentText}
            onChange={setCommentText}
            groupId={groupId}
            groupSlug={groupSlug}
            hideMentionAll={commentAnonymous}
            onSubmit={submitComment}
            onEscape={onEscape}
            onPasteFiles={commentFiles.addFiles}
            placeholder={commentAnonymous ? "Kommenter anonymt..." : "Skriv en kommentar..."}
            disabled={isSubmitting}
            multiline
            hideFormattingIcon
            /* `text-content leading-normal`, the size AND line-height the sent
               comment renders at (`CommentItem`) — this was the one comment box
               OSO-629 missed, still typing at 14px against a 16px result on
               every post card and in the lightbox. */
            className="w-full bg-muted rounded-2xl px-4 py-2 pr-10 text-content leading-normal text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-surface transition-colors"
          />
          <SubmitTooltip label="Send kommentar" className="absolute right-2 bottom-1.5">
            <button
              type="submit"
              disabled={!canSubmitComment}
              aria-label="Send kommentar"
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                !canSubmitComment
                  ? "text-subtle-foreground cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </SubmitTooltip>
        </div>
        {commentFiles.attachments.length > 0 && (
          <AttachmentTray
            attachments={commentFiles.attachments}
            onRemove={commentFiles.remove}
            onMove={commentFiles.move}
            size="sm"
          />
        )}
        {/* The write's own refusal, which this box never used to show: the
            text simply came back and nothing said why (OSO-563). */}
        {error && (
          <p role="alert" className="mt-1 text-xs text-destructive">{error}</p>
        )}
        {commentFiles.error && (
          <p className="mt-1 text-xs text-destructive">{commentFiles.error}</p>
        )}
        <SubmitHint value={commentText} />
      </form>
      <AddAttachmentButton
        onFiles={commentFiles.addFiles}
        label="Tilføj billede til kommentar"
        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-subtle-foreground hover:bg-muted hover:text-green-500 transition-colors"
      />
      {anonymousAllowed && (
        <button
          type="button"
          onClick={() => setCommentAnonymous((v) => !v)}
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
            commentAnonymous
              ? "bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400"
              : "text-subtle-foreground hover:bg-muted"
          }`}
          title={commentAnonymous ? "Slå anonymt fra" : "Kommenter anonymt"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </button>
      )}
    </div>
  );
}
